import React, { useState } from "react";
import { submitContact } from "../../api/contact";
import EnquiryFormFields from "../forms/EnquiryFormFields";

interface AboutAndContactFormProps {
  sourcePage?: string;
  defaultSubject?: string;
}

const AboutAndContactForm: React.FC<AboutAndContactFormProps> = ({
  sourcePage,
  defaultSubject,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSubmitting(true);

    try {
      await submitContact({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        subject: defaultSubject
          ? `Property enquiry: ${defaultSubject}`
          : "Property enquiry",
        message: message.trim(),
        sourcePage,
      });

      setSuccess(true);
      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send your enquiry"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-24 relative bg-bg-white">
      <div className="max-w-[1200px] mx-auto px-[5%] flex gap-12 flex-col md:flex-row">
        <div className="flex-1 pl-6 border-l-4 border-l-solid border-l-brand-yellow">
          <span className="font-manrope text-xs tracking-wider uppercase text-text-slate block mb-2">
            The Agency
          </span>
          <h2 className="page-section-heading mb-4">About Us</h2>
          <p className="font-manrope text-base leading-relaxed text-text-slate mb-8">
            We are the bridge between discerning tenants and London's finest
            properties. Our mission is to remove friction from the rental
            process through precision technology and dedicated human support. We
            don't just find you a house; we engineer your move.
          </p>
          <div className="flex flex-wrap gap-6">
            <div className="flex flex-col px-4 py-3 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.06)]">
              <span className="page-stat text-brand-gold">Propertymark</span>
              <span className="font-manrope text-xs uppercase tracking-wider text-text-slate">
                Protected
              </span>
            </div>
            <div className="flex flex-col px-4 py-3 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.06)]">
              <span className="page-stat text-brand-gold">Mayfair</span>
              <span className="font-manrope text-xs uppercase tracking-wider text-text-slate">
                Office
              </span>
            </div>
          </div>
        </div>
        <div className="flex-1">
          <div className="bg-bg-porcelain p-6 border border-solid border-[rgba(0,0,0,0.06)]">
            <h3 className="font-teko text-xl mb-4">Begin Enquiry</h3>
            <form className="grid gap-3" onSubmit={handleSubmit}>
              {success && (
                <p className="font-manrope text-sm text-green-700 bg-green-50 p-3 border border-green-200">
                  Thank you — your enquiry has been sent.
                </p>
              )}
              {error && (
                <p className="font-manrope text-sm text-red-700 bg-red-50 p-3 border border-red-200">
                  {error}
                </p>
              )}
              <EnquiryFormFields
                name={name}
                email={email}
                phone={phone}
                message={message}
                onNameChange={setName}
                onEmailChange={setEmail}
                onPhoneChange={setPhone}
                onMessageChange={setMessage}
                submitting={submitting}
              />
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutAndContactForm;
